import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Avatar } from '../../../layout/Avatar.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { Icon } from '../../../layout/Icon.jsx';
import { formatIQD } from '../../../shared/format.js';
import { useDuration } from '../../../shared/useDuration.js';
import { LiveBillBadge } from './LiveBillBadge.jsx';
import { ChildEditModal } from './ChildEditModal.jsx';

function statusVariant(s) {
  if (s === 'overstay' || s === 'expired') return 'danger';
  if (s === 'has_pending') return 'orange';
  return 'success';
}
function statusLabel(s) {
  if (s === 'overstay' || s === 'expired') return 'تجاوز';
  if (s === 'has_pending') return 'معلقة';
  return 'فعّال';
}

function genderLabel(g) {
  if (g === 'male') return 'ذكر';
  if (g === 'female') return 'أنثى';
  return g || '';
}

function resolvePhoto(url) {
  if (!url) return null;
  if (url.startsWith('http') || url.startsWith('/api/') || url.startsWith('/uploads/')) return url;
  return `/api/children/photos/${url.replace(/^\//, '')}`;
}

export function ActiveCard({ session }) {
  const [editOpen, setEditOpen] = useState(false);
  // Backend populates `childId` / `guardianId` with full objects.
  const child = session.childId || session.child || {};
  const guardian = session.guardianId || session.guardian || {};
  const duration = useDuration(session.startTime, 1000);

  // التجاوز يأتي محسوباً من الخادم (مدة الباقة)؛ القيم القديمة لم تكن تُضبط إطلاقاً.
  const isOverstay = !!session.overDuration
    || (session.expectedEndTime && new Date(session.expectedEndTime).getTime() < Date.now());
  const hasPending = (session.pendingAmount || 0) > 0 || session.hasPendingInvoices;
  const status = isOverstay ? 'overstay' : hasPending ? 'has_pending' : 'active';

  const photoUrl = resolvePhoto(child.photoUrl);

  return (
    <div
      className="card relative p-3.5"
      style={{
        borderColor:
          status === 'overstay' ? '#FECACA' : status === 'has_pending' ? '#FED7AA' : '#EFE7D6'
      }}
    >
      <div className="absolute top-2.5 left-2.5 flex gap-1">
        {Number(session.groupSize) > 1 ? (
          <Pill variant="muted">كارت مشترك ×{session.groupSize}</Pill>
        ) : null}
        {session.subscriptionCovered ? <Pill variant="info">اشتراك شهري</Pill> : null}
        <Pill variant={statusVariant(status)}>{statusLabel(status)}</Pill>
      </div>

      <div className="flex flex-col items-center gap-1.5">
        <Avatar src={photoUrl} name={child.name || '?'} id={child._id || child.id} size="lg" />
        <div className="font-bold text-[14px]">{child.name || '—'}</div>
        <div className="text-[11px] text-ink-3">
          {child.age ? `${child.age} سنوات` : ''}
          {child.gender ? ` • ${genderLabel(child.gender)}` : ''}
        </div>
      </div>

      <div className="border-t border-line my-3" />

      <div className="grid grid-cols-2 gap-y-1.5 gap-x-2 text-[12px]">
        <div className="text-ink-3">ولي الأمر</div>
        <div className="font-semibold text-end truncate">{guardian.name || '—'}</div>

        {guardian.phone ? (
          <>
            <div className="text-ink-3">الهاتف</div>
            <div className="font-mono font-semibold text-end" dir="ltr">{guardian.phone}</div>
          </>
        ) : null}

        <div className="text-ink-3">الكارت</div>
        <div className="font-mono font-semibold text-end" dir="ltr">{session.cardUid || session.cardId?.uid || session.card?.uid || '—'}</div>

        {session.entrySource === 'gate' ? (
          <>
            <div className="text-ink-3">الدخول</div>
            <div className="font-semibold text-end">عبر البوابة</div>
          </>
        ) : null}

        {Number(session.companionsCount) > 0 ? (
          <>
            <div className="text-ink-3">المرافقون</div>
            <div className="font-semibold text-end">
              {session.companionsCount}
              {Number(session.companionsAmount) > 0 ? ` • ${formatIQD(session.companionsAmount)}` : ''}
            </div>
          </>
        ) : null}

        <div className="text-ink-3">المدة</div>
        <div className="font-mono font-semibold text-end" style={{ color: isOverstay ? '#B91C1C' : '#1A1A1A' }}>
          {duration.formatted}
        </div>

        <div className="text-ink-3">المبلغ الحالي</div>
        <div className="text-end">
          <LiveBillBadge session={session} />
        </div>
      </div>

      {hasPending ? (
        <div className="mt-2 px-2 py-1.5 rounded-lg flex items-center justify-between" style={{ background: '#FEF2F2' }}>
          <span className="text-[11px] text-red">فواتير معلقة</span>
          <b className="text-red text-[12px]">{formatIQD(session.pendingAmount || 0)}</b>
        </div>
      ) : null}

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          className="btn flex-1 justify-center"
          style={{ padding: '6px', fontSize: 12 }}
          onClick={() => setEditOpen(true)}
        >
          <Icon name="edit" size={13} /> تعديل
        </button>
        <Link
          to={`/checkout/${session._id || session.id}`}
          className="btn btn-primary flex-1 justify-center"
          style={{ padding: '6px', fontSize: 12 }}
        >
          <Icon name="checkout" size={13} /> تسوية وخروج
        </Link>
      </div>

      <ChildEditModal open={editOpen} session={session} onClose={() => setEditOpen(false)} />
    </div>
  );
}
